// src/pages/Courses.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    courseName: '',
    description: '',
    duration: '',
    courseFee: '',
    icon: 'fa-graduation-cap',
    active: true,
    eligibility: '',
    documents: '',
    featuredImage: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('fa-graduation-cap');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentTime, setCurrentTime] = useState('');

  const iconOptions = [
    'fa-graduation-cap', 'fa-laptop-code', 'fa-chart-line', 'fa-calculator',
    'fa-flask', 'fa-microscope', 'fa-book', 'fa-paint-brush',
    'fa-music', 'fa-robot', 'fa-database', 'fa-network-wired'
  ];

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const q = query(collection(db, 'courses'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const courseData = [];
      snapshot.forEach((doc) => {
        courseData.push({ id: doc.id, ...doc.data() });
      });
      setCourses(courseData);
      setLoading(false);
    });

    return () => {
      clearInterval(clockInterval);
      unsubscribe();
    };
  }, []);

  const resetForm = () => {
    setFormData({
      courseName: '',
      description: '',
      duration: '',
      courseFee: '',
      icon: 'fa-graduation-cap',
      active: true,
      eligibility: '',
      documents: '',
      featuredImage: ''
    });
    setImageFile(null);
    setImagePreview('');
    setSelectedIcon('fa-graduation-cap');
    setEditingCourse(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEditModal = (item) => {
    setEditingCourse(item);
    setFormData({
      courseName: item.courseName || '',
      description: item.description || '',
      duration: item.duration || '',
      courseFee: item.courseFee || '',
      icon: item.icon || 'fa-graduation-cap',
      active: item.active !== false,
      eligibility: item.eligibility || '',
      documents: item.documents || '',
      featuredImage: item.featuredImage || ''
    });
    setSelectedIcon(item.icon || 'fa-graduation-cap');
    if (item.featuredImage) setImagePreview(item.featuredImage);
    setModalOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsUploading(true);

    try {
      let featuredImageUrl = formData.featuredImage;

      if (imageFile) {
        const storageRef = ref(storage, `courses/${Date.now()}_${imageFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              featuredImageUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      const courseData = {
        courseName: formData.courseName,
        description: formData.description,
        duration: formData.duration,
        courseFee: parseInt(formData.courseFee) || 0,
        icon: selectedIcon,
        active: formData.active,
        eligibility: formData.eligibility || '',
        documents: formData.documents || '',
        featuredImage: featuredImageUrl,
        updatedAt: new Date()
      };

      if (editingCourse) {
        if (formData.featuredImage && imageFile && formData.featuredImage !== featuredImageUrl) {
          try { await deleteObject(ref(storage, formData.featuredImage)); } catch (e) {}
        }
        await updateDoc(doc(db, 'courses', editingCourse.id), courseData);
        alert('Course updated successfully!');
      } else {
        courseData.createdAt = new Date();
        await addDoc(collection(db, 'courses'), courseData);
        alert('Course added successfully!');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving course:', error);
      alert('Error saving course: ' + error.message);
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id, featuredImageUrl) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    setLoading(true);
    try {
      if (featuredImageUrl) {
        try { await deleteObject(ref(storage, featuredImageUrl)); } catch (e) {}
      }
      await deleteDoc(doc(db, 'courses', id));
      alert('Course deleted successfully!');
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Error deleting course: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const getFilteredCourses = () => {
    if (filterStatus === 'all') return courses;
    return courses.filter(c => c.active === (filterStatus === 'active'));
  };

  const getStats = () => {
    const total = courses.length;
    const active = courses.filter(c => c.active === true).length;
    const totalFee = courses.reduce((sum, c) => sum + (parseInt(c.courseFee) || 0), 0);
    return { total, active, totalFee };
  };

  const stats = getStats();
  const filteredCourses = getFilteredCourses();

  const styles = `
    .courses-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .management-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }

    .filter-select {
      padding: 10px 20px;
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      outline: none;
      transition: all 0.2s;
    }

    .filter-select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .btn-action-trigger {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }

    .btn-action-trigger:hover {
      transform: translateY(-3px);
    }

    .btn-blue {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
    }

    .btn-blue:hover {
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .courses-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
      gap: 28px;
    }

    .course-card {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    .course-card:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.65);
      box-shadow: 0 16px 36px 0 rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .course-card-banner {
      padding: 32px 24px;
      text-align: center;
      color: white;
      font-size: 44px;
      position: relative;
      background-size: cover;
      background-position: center;
      min-height: 160px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
    }

    .course-card-banner i {
      position: relative;
      z-index: 1;
    }

    .featured-image-preview {
      width: 100%;
      height: 100%;
      object-fit: cover;
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0.3;
    }

    .status-badge-indicator {
      position: absolute;
      top: 16px;
      right: 16px;
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 0.7rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border: 1px solid rgba(255,255,255,0.2);
      backdrop-filter: blur(4px);
      z-index: 2;
    }

    .course-card-content {
      padding: 24px;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .course-card-title {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.3rem;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 10px;
    }

    .course-card-description {
      color: #334155;
      font-size: 0.88rem;
      line-height: 1.5;
      margin-bottom: 16px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .course-card-details-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      padding: 14px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 12px;
      border: 1px solid rgba(255,255,255,0.3);
    }

    .course-detail-unit {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      text-align: center;
      flex: 1;
    }

    .course-detail-unit i {
      font-size: 18px;
    }

    .course-detail-unit span {
      font-size: 0.85rem;
      font-weight: 700;
      color: #0f172a;
    }

    .course-detail-unit small {
      font-size: 0.7rem;
      color: #64748b;
      font-weight: 600;
      text-transform: uppercase;
    }

    .detail-badge {
      background: rgba(14, 165, 233, 0.08);
      border-radius: 8px;
      padding: 8px 12px;
      margin-bottom: 10px;
    }

    .detail-badge i {
      width: 24px;
      color: #0ea5e9;
    }

    .eligibility-list, .documents-list {
      list-style: none;
      padding-left: 24px;
      margin-top: 5px;
    }

    .eligibility-list li, .documents-list li {
      font-size: 0.75rem;
      color: #334155;
      margin-bottom: 4px;
      position: relative;
    }

    .eligibility-list li:before, .documents-list li:before {
      content: "•";
      color: #0ea5e9;
      font-weight: bold;
      position: absolute;
      left: -12px;
    }

    .course-card-actions {
      display: flex;
      gap: 10px;
      padding-top: 16px;
      border-top: 1px solid rgba(14, 165, 233, 0.1);
      margin-top: auto;
    }

    .btn-row-action {
      flex: 1;
      padding: 8px 14px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;
    }

    .btn-row-edit {
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #0ea5e9;
    }

    .btn-row-edit:hover {
      background: #0ea5e9;
      color: white;
    }

    .btn-row-delete {
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      color: #f43f5e;
    }

    .btn-row-delete:hover {
      background: #f43f5e;
      color: white;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      grid-column: 1/-1;
    }

    .empty-state i {
      font-size: 54px;
      color: #64748b;
      margin-bottom: 16px;
      display: block;
    }

    .empty-state h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.2rem;
      margin-bottom: 6px;
      color: #0f172a;
    }

    .empty-state p {
      color: #64748b;
      font-size: 0.88rem;
      margin-bottom: 20px;
    }

    /* Modal Styles - Positioned at Top */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      min-height: 100vh;
      background: rgba(15, 23, 42, 0.6);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 10000;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 40px 20px;
      overflow-y: auto;
      animation: fadeIn 0.3s ease;
    }

    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 100%;
      max-width: 750px;
      max-height: none;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideDown 0.3s ease;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .modal-viewport-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .modal-viewport-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.4rem;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .modal-viewport-header h2 i {
      color: #0ea5e9;
    }

    .modal-close-btn {
      width: 40px;
      height: 40px;
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      border-radius: 12px;
      color: #f43f5e;
      cursor: pointer;
      font-size: 1.2rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close-btn:hover {
      background: #f43f5e;
      color: white;
      transform: rotate(90deg);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }

    .form-group {
      margin-bottom: 4px;
    }

    .form-group.full-width {
      grid-column: 1 / -1;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .form-group textarea {
      resize: vertical;
      min-height: 80px;
    }

    .file-upload-wrapper {
      position: relative;
      border: 2px dashed rgba(14, 165, 233, 0.3);
      border-radius: 12px;
      padding: 16px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.3);
    }

    .file-upload-wrapper:hover {
      border-color: #0ea5e9;
      background: rgba(255, 255, 255, 0.5);
    }

    .file-upload-wrapper input[type="file"] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    .file-upload-wrapper .upload-icon {
      font-size: 1.5rem;
      color: #0ea5e9;
      margin-bottom: 4px;
      display: block;
    }

    .file-upload-wrapper .upload-text {
      color: #64748b;
      font-size: 0.8rem;
      font-weight: 600;
    }

    .file-upload-wrapper .upload-text span {
      color: #0ea5e9;
      font-weight: 700;
    }

    .image-preview-wrapper {
      margin-top: 12px;
      position: relative;
      display: inline-block;
      border-radius: 12px;
      overflow: hidden;
      border: 2px solid rgba(14, 165, 233, 0.2);
    }

    .image-preview-wrapper img {
      width: 120px;
      height: 120px;
      object-fit: cover;
      display: block;
    }

    .image-preview-wrapper .remove-image {
      position: absolute;
      top: 4px;
      right: 4px;
      background: rgba(244, 63, 94, 0.9);
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .image-preview-wrapper .remove-image:hover {
      transform: scale(1.1);
      background: #f43f5e;
    }

    .upload-progress {
      margin-top: 12px;
      background: rgba(14, 165, 233, 0.1);
      border-radius: 8px;
      height: 6px;
      overflow: hidden;
    }

    .upload-progress .progress-bar {
      height: 100%;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      transition: width 0.3s ease;
      border-radius: 8px;
    }

    .icon-selector-matrix {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 8px;
      margin-top: 4px;
    }

    .icon-matrix-tile {
      padding: 12px;
      text-align: center;
      border: 2px solid rgba(14, 165, 233, 0.15);
      background: rgba(255, 255, 255, 0.4);
      border-radius: 10px;
      cursor: pointer;
      color: #64748b;
      transition: all 0.2s ease;
      font-size: 1.1rem;
    }

    .icon-matrix-tile:hover {
      border-color: #0ea5e9;
      background: rgba(255, 255, 255, 0.8);
      transform: translateY(-2px);
    }

    .icon-matrix-tile.selected {
      border-color: #0ea5e9;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);
      transform: translateY(-2px);
    }

    .checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      padding: 4px 0;
    }

    .checkbox-wrapper input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #0ea5e9;
      cursor: pointer;
    }

    .checkbox-wrapper label {
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      cursor: pointer;
      margin: 0;
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .btn-cancel {
      padding: 10px 24px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit {
      padding: 10px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-submit .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 1024px) {
      .courses-grid {
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      }
    }

    @media (max-width: 768px) {
      .courses-grid {
        grid-template-columns: 1fr;
      }
      .icon-selector-matrix {
        grid-template-columns: repeat(4, 1fr);
      }
      .form-grid {
        grid-template-columns: 1fr;
      }
      .management-toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .management-toolbar > div {
        justify-content: center;
      }
      .modal-viewport-box {
        padding: 24px;
        margin: 10px;
      }
      .modal-viewport-header h2 {
        font-size: 1.2rem;
      }
      .modal-footer {
        flex-direction: column;
      }
      .modal-footer button {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  if (loading && courses.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="courses-page">
        <div className="welcome-hero">
          <h1>📚 Courses Management</h1>
          <p>Manage course details, fees, eligibility criteria, and academic programs seamlessly.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="stats-matrix" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="matrix-card card-blue">
            <div>
              <div className="matrix-value">{stats.total}</div>
              <div className="matrix-label">Total Courses</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-book-open"></i></div>
          </div>
          <div className="matrix-card card-emerald">
            <div>
              <div className="matrix-value">{stats.active}</div>
              <div className="matrix-label">Active Courses</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-toggle-on"></i></div>
          </div>
          <div className="matrix-card card-amber">
            <div>
              <div className="matrix-value">₹{stats.totalFee.toLocaleString()}</div>
              <div className="matrix-label">Total Value</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-wallet"></i></div>
          </div>
        </div>

        <div className="management-toolbar">
          <select 
            className="filter-select" 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Courses</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
          <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
            <i className="fas fa-plus-circle"></i> Add New Course
          </button>
        </div>

        <div className="courses-grid">
          {filteredCourses.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-folder-open"></i>
              <h3>No Courses Found</h3>
              <p>There are no courses matching the selected filters.</p>
              <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
                <i className="fas fa-plus-circle"></i> Add First Course
              </button>
            </div>
          ) : (
            filteredCourses.map((item) => {
              const bannerColorSchemes = [
                'linear-gradient(135deg, #0ea5e9, #0284c7)',
                'linear-gradient(135deg, #10b981, #059669)',
                'linear-gradient(135deg, #f59e0b, #d97706)',
                'linear-gradient(135deg, #8b5cf6, #6d28d9)'
              ];
              const colorIndex = courses.indexOf(item) % bannerColorSchemes.length;
              const eligibilityList = item.eligibility ? item.eligibility.split('\n') : ['10+2 with minimum 50% marks', 'Valid entrance exam score'];
              const documentsList = item.documents ? item.documents.split('\n') : ['10th & 12th Mark Sheets', 'Transfer Certificate', 'Migration Certificate'];

              return (
                <div className="course-card" key={item.id}>
                  <div className="course-card-banner" style={{
                    background: bannerColorSchemes[colorIndex],
                    ...(item.featuredImage ? { backgroundImage: `url('${item.featuredImage}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : {})
                  }}>
                    {item.featuredImage && <div className="featured-image-preview"></div>}
                    <i className={`fas ${item.icon || 'fa-graduation-cap'}`}></i>
                    <div className="status-badge-indicator" style={{
                      background: item.active ? 'rgba(16,185,129,0.4)' : 'rgba(100,116,139,0.4)'
                    }}>
                      {item.active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div className="course-card-content">
                    <h3 className="course-card-title">{item.courseName || 'Untitled Course'}</h3>
                    <p className="course-card-description">{item.description || 'No description available for this course.'}</p>
                    
                    <div className="course-card-details-row">
                      <div className="course-detail-unit">
                        <i className="fas fa-clock" style={{ color: '#0ea5e9' }}></i>
                        <span>{item.duration || 'N/A'}</span>
                        <small>Duration</small>
                      </div>
                      <div className="course-detail-unit">
                        <i className="fas fa-indian-rupee-sign" style={{ color: '#10b981' }}></i>
                        <span>{item.courseFee ? `₹${parseInt(item.courseFee).toLocaleString()}` : 'N/A'}</span>
                        <small>Course Fee</small>
                      </div>
                    </div>
                    
                    <div className="detail-badge">
                      <i className="fas fa-graduation-cap"></i> <strong>Eligibility Criteria</strong>
                      <ul className="eligibility-list">
                        {eligibilityList.map((e, idx) => <li key={idx}>{e}</li>)}
                      </ul>
                    </div>
                    
                    <div className="detail-badge">
                      <i className="fas fa-file-alt"></i> <strong>Documents Required</strong>
                      <ul className="documents-list">
                        {documentsList.map((d, idx) => <li key={idx}>{d}</li>)}
                      </ul>
                    </div>
                    
                    <div className="course-card-actions">
                      <button className="btn-row-action btn-row-edit" onClick={() => openEditModal(item)}>
                        <i className="fas fa-pen-to-square"></i> Edit
                      </button>
                      <button className="btn-row-action btn-row-delete" onClick={() => handleDelete(item.id, item.featuredImage)}>
                        <i className="fas fa-trash-can"></i> Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Course Modal - Positioned at Top */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className={`fas ${editingCourse ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                  {editingCourse ? 'Edit Course Details' : 'Add New Course'}
                </h2>
                <button className="modal-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  {/* Course Name */}
                  <div className="form-group full-width">
                    <label>Course Name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.courseName}
                      onChange={(e) => setFormData({ ...formData, courseName: e.target.value })}
                      placeholder="e.g., B.Tech Computer Science"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div className="form-group full-width">
                    <label>Description <span className="required">*</span></label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="3"
                      placeholder="Enter course specifications..."
                      required
                    />
                  </div>

                  {/* Duration & Fee */}
                  <div className="form-group">
                    <label>Course Duration <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      placeholder="e.g., 4 Years (8 Semesters)"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Course Fee (₹) <span className="required">*</span></label>
                    <input
                      type="number"
                      value={formData.courseFee}
                      onChange={(e) => setFormData({ ...formData, courseFee: e.target.value })}
                      placeholder="e.g., 120000"
                      required
                    />
                  </div>

                  {/* Featured Image */}
                  <div className="form-group full-width">
                    <label>Featured Image</label>
                    <div className="file-upload-wrapper">
                      <i className="fas fa-cloud-upload-alt upload-icon"></i>
                      <div className="upload-text">
                        Drag & drop or <span>browse</span> to upload image
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </div>
                    {imagePreview && (
                      <div className="image-preview-wrapper">
                        <img src={imagePreview} alt="Preview" />
                        <button
                          type="button"
                          className="remove-image"
                          onClick={() => {
                            setImagePreview('');
                            setImageFile(null);
                            setFormData({ ...formData, featuredImage: '' });
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Icon Selector */}
                  <div className="form-group full-width">
                    <label>Course Icon <span className="required">*</span></label>
                    <div className="icon-selector-matrix">
                      {iconOptions.map(icon => (
                        <div
                          key={icon}
                          className={`icon-matrix-tile ${selectedIcon === icon ? 'selected' : ''}`}
                          onClick={() => setSelectedIcon(icon)}
                        >
                          <i className={`fas ${icon}`}></i>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Eligibility */}
                  <div className="form-group full-width">
                    <label>Eligibility Criteria (One per line)</label>
                    <textarea
                      value={formData.eligibility}
                      onChange={(e) => setFormData({ ...formData, eligibility: e.target.value })}
                      rows="3"
                      placeholder="10+2 with minimum 50% marks&#10;Valid entrance exam score&#10;Age limit: 17-25 years"
                    />
                  </div>

                  {/* Documents */}
                  <div className="form-group full-width">
                    <label>Documents Required (One per line)</label>
                    <textarea
                      value={formData.documents}
                      onChange={(e) => setFormData({ ...formData, documents: e.target.value })}
                      rows="3"
                      placeholder="10th Mark Sheet&#10;12th Mark Sheet&#10;Transfer Certificate&#10;Migration Certificate"
                    />
                  </div>

                  {/* Active Status */}
                  <div className="form-group full-width">
                    <div className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        id="activeCheckbox"
                      />
                      <label htmlFor="activeCheckbox">Active (Show on live website)</label>
                    </div>
                  </div>
                </div>

                {isUploading && uploadProgress > 0 && (
                  <div className="upload-progress">
                    <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <span className="spinner"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingCourse ? 'Update Course' : 'Create Course'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Courses;